// ScreenHeader — THE header block. One shape on every screen (7 Sept 2026):
//
//   title · one-line subtitle · [right-side actions] · [About this screen]
//   [optional extra row: period chips, desk switch, a freshness line]
//
// Before this each screen drew its own header — five title weights, three
// subtitle sizes, paragraphs where a sentence belonged. Screens pass words and
// controls; the typography and spacing are decided here, once.
//
// `actions` sit at the right of the title row. `help` adds the ScreenHelp
// toggle beside them, and its text opens beneath the row (see ScreenHelp for
// how the two share the flex row). `children` is anything that belongs under
// the title but is not the title — the chip row on the reports, the status
// line on Chain and Exchanges.

import type { ReactNode } from 'react';
import { ScreenHelp } from './ScreenHelp';
import { uiV2 } from '../lib/ui-v2';

// With the rebuild's batch 1 on, a screen's title is the page role (24/30), the size its rebuilt PageHead uses —
// `title` is a panel's size there (16). Off, main's classes exactly.
const PAGE_TITLE = uiV2(1) ? 'text-page font-semibold' : 'text-title font-medium lg:font-semibold lg:tracking-[-0.01em]';

export function ScreenHeader({ title, subtitle, help, actions, children, className = '' }: {
  title: ReactNode;
  subtitle?: ReactNode;
  help?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <header className={`space-y-2.5 ${className}`}>
      {/* ON A PHONE THE TITLE LINE CARRIES THE ABOUT TOGGLE at its right end
          and the actions, when there are any, take a row beneath the words
          (operator, 20 Sept 2026: "remove clutter" — the row of its own the
          toggle had since 15 Sept was a 32px line holding one small word on
          every screen). At lg+ the one row stands: title left, actions and
          About right. ScreenHelp renders its button and its open panel
          straight into this flex row (display: contents), ordered so the
          button sits by the title and the panel wraps beneath everything. */}
      <div className="flex flex-wrap items-start gap-x-3 lg:gap-x-2">
        <h1 className={`order-1 min-w-0 flex-1 ${PAGE_TITLE}`}>{title}</h1>
        {/* The subtitle is a line of its own at every width — beside the
            toggle it was squeezed into a 200px column with a blank beside
            it (review, 20 Sept 2026). */}
        {subtitle && <p className="order-3 basis-full mt-0.5 text-meta text-tg-hint lg:order-5">{subtitle}</p>}
        {actions && (
          <div className="order-4 basis-full mt-2 flex flex-wrap items-center gap-2 lg:order-3 lg:basis-auto lg:mt-0 lg:shrink-0 lg:justify-end">
            {actions}
          </div>
        )}
        {help && <ScreenHelp>{help}</ScreenHelp>}
      </div>
      {children}
    </header>
  );
}
